import { GAMMA_MEDIUM_CHAIN } from "./gamma";
import { consecutiveHourlyWindows, uniqueOrderedHours } from "./hourly-windows";
/**
 * Ski Touring Scoring v4 - Purely Multiplicative Architecture
 *
 * hourly_score = 100 × F_descent × F_trailBreak × F_freezeThaw × F_coverage
 *                    × F_wind × F_visibility × F_precip × F_comfort × F_solar
 *
 * Each factor is a smooth continuous multiplier in [~0.05, ~1.05].
 * Curves are sigmoid, Gaussian, or exponential - no step functions.
 * Daily score = (best 5h window × 0.40 + daylight average × 0.60) / 10.
 *
 * F_descent is the descent-surface quality [0.60, 1.05], built on the shared
 * fresh-snow machinery (decay, wet-bulb quality, preservation, wind transport
 * - see fresh-snow.ts) with a corn-cycle floor and a breakable-crust penalty.
 * F_trailBreak is the ascent-side cost of very deep unconsolidated fresh, so
 * the model distinguishes "great skiing, hard skin up" from "easy skin,
 * rubbish descent".
 *
 * Safety indicators (avalanche, wind loading) are computed separately and never modify the score.
 */

import type { NormalisedHour, DailySources, WeatherHourly } from "./daily-obs-types";
import {
  buildLookback,
  computeEffectiveFresh,
  detectCorn,
  detectBreakableCrust,
  CRUST_COVERAGE_THRESHOLD_CM,
} from "./fresh-snow";
import { composeScore, defineChain } from "./compose-score";
import {
  naturalDepthAtElevation,
  bandAnchorsAtHour,
  DEFAULT_B1_PARAMS,
  DEPTH_HAIRCUT_SPAN_M,
  DEPTH_HAIRCUT_FLOOR,
  type BandAnchors,
  type DepthAnchors,
} from "./snow-depth-profile";

// Touring's chain, declared as data (tracker P3-19); classes declared, not enforced (Phase 7).
// Gamma derived from the 9 declared factors (MEDIUM) under the Phase 6
// all-or-none policy (tracker P6-1, audit 90.3): touring carried the exact
// chain-length pathology gamma fixes. The 0-100 scale constant is applied
// outside composeScore so gamma compresses the 0-1 quality product.
export const TOURING_CHAIN = defineChain({
  sport: "touring",
  gamma: GAMMA_MEDIUM_CHAIN, // Pinned calibration; factor-list refactors must not change the scale.
  factors: [
    { key: "descent", class: "driver" },
    { key: "trailBreak", class: "driver", note: "passes clause 2 cleanly (13 B1)" },
    { key: "freezeThaw", class: "driver" },
    { key: "coverage", class: "driver" },
    { key: "wind", class: "driver" },
    { key: "visibility", class: "driver", note: "immersion stacking with solar is P7-7's target" },
    { key: "precip", class: "driver" },
    { key: "comfort", class: "modifier", note: "P7-8: bounded enjoyment modifier - neutral across the gear-mitigated range, bites only below -25C to a 0.85 floor; deep cold carried by the wind-chill term + extreme_cold flag (13 V2)" },
    { key: "solar", class: "driver", note: "P7-7: flat-light tier-migrated quality - a high-cloud overcast deck degrades surface definition on open alpine terrain (0.80 floor); the low/mid immersion case is F_visibility's (13 V1 / 13 B2)" },
  ],
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TouringDailyResult = {
  score: number;
  label: string;
  snowScore: number;
  windowScore: number;
  freezeThawScore: number;
  bestWindowStart: number | null;
  suppressed: boolean;
  suppressionReason: string | null;
  coverageClassification: "pass" | "marginal" | "fail" | "unknown";
  factors: TouringFactor[];
  elevationProfile: ElevationBandHour[][];
  stabilityDisclaimer: true;
  safety: TouringSafetyIndicator[];
};

export type TouringFactor = {
  key: string;
  label: string;
  score: number;
  weight: number;
  value: string;
};

export type ElevationBandHour = {
  elevation_m: number;
  band: "valley" | "mid" | "summit";
  hour: number;
  temp_c: number;
  wind_kmh: number;
  precip_type: "snow" | "rain" | "mixed" | "none";
  cloud_immersed: boolean;
  visibility_m: number;
  freezing_level_m: number;
};

export type ElevationConfig = {
  elevation_m: number;
  elevation_base_m: number | null;
  elevation_summit_m: number | null;
  /**
   * Editorial mid-mountain (condition_locations.mid_elevation_m). When present it
   * anchors the touring "mid" band (descent/comfort/solar), so touring's mid
   * agrees with the piste/off-piste mid instead of an arithmetic midpoint that
   * can diverge. Absent → falls back to the (valley+summit)/2 midpoint.
   */
  mid_elevation_m?: number | null;
};

export type TouringSafetyIndicator = {
  key: string;
  severity: "green" | "amber" | "red";
  label: string;
  detail: string | null;
  disclaimer: string | null;
};

export const AVALANCHE_SERVICES: Record<string, { url: string; name: string }> = {
  France: { url: "https://meteofrance.com/meteo-montagne", name: "Meteo-France" },
  Switzerland: { url: "https://slf.ch", name: "SLF" },
  Austria: { url: "https://lawine.at", name: "Austrian Avalanche Warning" },
  Italy: { url: "https://aineva.it", name: "AINEVA" },
  "United States": { url: "https://avalanche.org", name: "National Avalanche Center" },
  Canada: { url: "https://avalanche.ca", name: "Avalanche Canada" },
  Norway: { url: "https://varsom.no", name: "Varsom" },
  Sweden: { url: "https://naturvaardsverket.se", name: "Naturvårdsverket" },
  Scotland: { url: "https://sais.gov.uk", name: "SAIS" },
  Japan: { url: "https://nadare.jp", name: "Japan Avalanche Network" },
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WIND_MIDPOINT_KMH = 50;
const WIND_STEEPNESS = 10;
const WIND_FLOOR = 0.05;

const VIS_MIDPOINT_M = 1200;
const VIS_STEEPNESS = 1000;
const VIS_FLOOR = 0.08;

// Skinning comfort is a bounded enjoyment modifier (clause 3, audit 13 V2):
// neutral across the gear-mitigated touring range, biting only at genuine
// extremes. Cold below COMFORT_EXTREME_COLD_C degrades the day regardless of
// gear (transitions, eating and drinking become punishing; experts re-pick or
// shorten) - a real but bounded dent to COMFORT_FLOOR. The gear-mitigated
// -12..-25C band is NOT charged (clause 4: charging it permanently penalises
// cold regions, the exact failure mode the constitution names). The wind
// factor's wind-chill term (< -28C summit) and the extreme_cold flag carry the
// deep-cold hazard and degradation.
const COMFORT_FLOOR = 0.85;
const COMFORT_EXTREME_COLD_C = -25;
const COMFORT_EXTREME_COLD_SLOPE = 0.015; // per C below -25C: -30C -> 0.925, >= -35C -> 0.85 floor

// Flat-light tier-migrated quality (audit 13 V1 / 13 B2): a high-cloud overcast
// deck flattens surface definition on the open alpine terrain where touring
// descents happen. Bounded a touch below the 0.85 enjoyment floor because flat
// light genuinely degrades the descent (a quality driver, not an enjoyment
// modifier), but never the old 0.50 sunshine tax that halved every grey day.
const SOLAR_FLAT_LIGHT_FLOOR = 0.80;

// Descent quality: settled snow floors at 0.60, deep preserved powder
// saturates at 1.05. The corn cycle is the touring archetype, so a detected
// corn day floors at 1.0 (the freeze-thaw factor grades the cycle itself).
const DESCENT_FLOOR = 0.60;
const DESCENT_RANGE = 0.45;
const DESCENT_SATURATION_CM = 10;
const DESCENT_CORN_FLOOR = 1.0;
const DESCENT_CRUST_PENALTY = 0.45;
const DESCENT_ANCHOR_HOUR = 12;

const SNOW_TRAIL_BREAK_ONSET_CM = 40;
const SNOW_TRAIL_BREAK_WIDTH = 500;
const SNOW_TRAIL_BREAK_FLOOR = 0.45;

const FREEZE_THAW_REFREEZE_OPTIMAL_C = -10;
const FREEZE_THAW_REFREEZE_SIGMA = 5;
const WARMING_OPTIMAL_RATE = 1.5;
const WARMING_SIGMA = 1.2;

const DEPTH_MIDPOINT_M = 0.50;
const DEPTH_STEEPNESS = 0.15;

const DAILY_BEST_WINDOW_WEIGHT = 0.40;
const DAILY_DAYLIGHT_AVG_WEIGHT = 0.60;
const TOURING_WINDOW_HOURS = 5;

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max);
}

function sigmoid(x: number, midpoint: number, steepness: number, floor: number): number {
  return floor + (1 - floor) / (1 + Math.exp((x - midpoint) / steepness));
}

function sigmoidRising(x: number, midpoint: number, steepness: number, floor: number): number {
  return floor + (1 - floor) / (1 + Math.exp(-(x - midpoint) / steepness));
}

function gaussian(x: number, center: number, sigma: number): number {
  return Math.exp(-((x - center) ** 2) / (2 * sigma ** 2));
}

/**
 * Phase-3 snow depth: re-point the day's per-hour `snow_depth_m` at the
 * INTERPOLATED natural depth at the touring mid band (`bands.mid` — the
 * representative descent surface, ski-touring-scoring-v4 §"Mid-mountain is the
 * representative descent surface"). Touring depth is the RAW query-elevation
 * depth today (no lapse on depth), so we simply override every hour's depth with
 * the single interp value. Touring is a NATURAL sport: no Method C (no man-made
 * base). The native-cell depth at the scored hour is the high
 * anchor for the B1 fallback inside `naturalDepthAtElevation`, but with ≥2 cleaned
 * band anchors the interp PRIMARY path is taken, so the anchor is informational.
 *
 * No-regression: when `bandAnchors` is absent or has <2 cleaned points the input
 * `hours` is returned UNCHANGED (referential identity), so the score is
 * byte-identical to the pre-Phase-3 raw-depth path. Null-depth hours stay null.
 */
function applyInterpDepthAtMid(
  hours: NormalisedHour[],
  bands: ReturnType<typeof deriveElevationBands>,
  queryElevM: number,
  bandAnchors?: BandAnchors,
): NormalisedHour[] {
  if (!bandAnchors) return hours;
  return hours.map((h) => {
    const hourlyAnchors = bandAnchorsAtHour(bandAnchors, h.hour);
    if (!hourlyAnchors || hourlyAnchors.points.length < 2 || h.weather.snow_depth_m == null) return h;
    const anchors: DepthAnchors = {
      dataElevM: queryElevM, anchorElevM: queryElevM,
      highDepthM: h.weather.snow_depth_m,
    };
    const naturalM = naturalDepthAtElevation(bands.mid, anchors, {
      bandAnchors: hourlyAnchors,
      dayHours: [h],
      b1: DEFAULT_B1_PARAMS,
      haircutSpanM: DEPTH_HAIRCUT_SPAN_M,
      haircutFloor: DEPTH_HAIRCUT_FLOOR,
    }).depthM;
    return { ...h, weather: { ...h.weather, snow_depth_m: naturalM } };
  });
}

// ---------------------------------------------------------------------------
// Labels (0-10 scale)
// ---------------------------------------------------------------------------

export function touringV4Label(score: number): string {
  if (score >= 9.0) return "Hero";
  if (score >= 7.5) return "Firing";
  if (score >= 6.5) return "Great";
  if (score >= 5.0) return "Good";
  if (score >= 3.5) return "Fair";
  if (score >= 2.0) return "Poor";
  return "Flat";
}

// ---------------------------------------------------------------------------
// Elevation model
// ---------------------------------------------------------------------------

function deriveElevationBands(config: ElevationConfig) {
  const summit = config.elevation_summit_m ?? config.elevation_m + 400;
  const valley = config.elevation_base_m ?? config.elevation_m - 800;
  // Prefer the editorial mid column (one source of truth with piste/off-piste);
  // fall back to the arithmetic midpoint when it is absent.
  const mid = config.mid_elevation_m ?? Math.round((valley + summit) / 2);
  return { valley, mid, summit, queryElev: config.elevation_m };
}

function lapseTemp(baseTemp: number, baseElev: number, targetElev: number): number {
  return baseTemp - 6.5 * (targetElev - baseElev) / 1000;
}

function deriveSummitWind(w: NormalisedHour["weather"]): number {
  return w.wind_speed_120m_kmh ?? w.wind_speed_10m_kmh * 1.6;
}

function deriveMidWind(w: NormalisedHour["weather"]): number {
  return w.wind_speed_80m_kmh ?? w.wind_speed_10m_kmh * 1.3;
}

function deriveSummitGust(w: NormalisedHour["weather"]): number {
  return w.wind_gust_10m_kmh * 1.3;
}

function isCloudImmersedMid(w: NormalisedHour["weather"]): boolean {
  return (w.cloud_cover_low_pct ?? 0) > 60 && (w.cloud_cover_mid_pct ?? 0) > 60;
}

function isCloudImmersedSummit(w: NormalisedHour["weather"]): boolean {
  return (w.cloud_cover_mid_pct ?? 0) > 70 ||
    ((w.cloud_cover_low_pct ?? 0) > 80 && (w.cloud_cover_high_pct ?? 0) > 80);
}

function getPrecipType(freezingLevel: number | null, bandElev: number): "snow" | "rain" | "mixed" | "none" {
  if (freezingLevel === null) return "none";
  if (bandElev > freezingLevel) return "snow";
  if (bandElev < freezingLevel - 200) return "rain";
  return "mixed";
}

function deriveVisibility(baseVis: number | null, band: "valley" | "mid" | "summit", cloudImmersed: boolean): number {
  const vis = baseVis ?? 20000;
  if (band === "valley") return vis;
  if (band === "mid") return cloudImmersed ? vis * 0.6 : vis;
  return cloudImmersed ? vis * 0.3 : vis * 0.7;
}

function windChill(tempC: number, windKmh: number): number {
  if (tempC >= 10 || windKmh < 4.8) return tempC;
  return 13.12 + 0.6215 * tempC - 11.37 * Math.pow(windKmh, 0.16) + 0.3965 * tempC * Math.pow(windKmh, 0.16);
}

// ---------------------------------------------------------------------------
// Suppression check (score forced to 0 for impossible conditions)
// ---------------------------------------------------------------------------

type SuppressionResult = { suppressed: boolean; reason: string | null };

function checkSuppressions(
  hours: NormalisedHour[],
  _bands: ReturnType<typeof deriveElevationBands>
): SuppressionResult {
  const daylight = hours.filter((h) => h.weather.is_day);
  if (daylight.length === 0) {
    return { suppressed: true, reason: "No daylight hours" };
  }

  const snowDepths = daylight
    .map((h) => h.weather.snow_depth_m)
    .filter((d): d is number => d !== null);
  if (snowDepths.length > 0 && snowDepths.every((d) => d < 0.10)) {
    return { suppressed: true, reason: "Insufficient snow cover" };
  }

  return { suppressed: false, reason: null };
}

// ---------------------------------------------------------------------------
// Factor 1: Descent Quality (constant for the day)
//
// Built on the shared fresh-snow machinery: quality-adjusted, decay-weighted,
// wind-penalised effective fresh snow, with a corn-cycle floor and a
// breakable-crust penalty. Anchored at midday - the typical descent time.
// ---------------------------------------------------------------------------

type DescentQualityResult = {
  factor: number;
  effectiveFreshCm: number;
  freshImpactCm: number;
  rainPenalised: boolean;
  cornDetected: boolean;
  crustSeverity: number;
  value: string;
};

function computeDescentQualityFactor(
  hours: NormalisedHour[],
  yesterdayHours: NormalisedHour[] | undefined,
  bands: ReturnType<typeof deriveElevationBands>,
  crustSeverityOverride?: number,
  anchorHour: number = DESCENT_ANCHOR_HOUR,
): DescentQualityResult {
  const daylight = hours.filter((h) => h.weather.is_day);

  // Raw recency-weighted fresh: drives trail breaking and avalanche proxies
  const todaySnowfall = hours.reduce((s, h) => s + (h.weather.snowfall_cm ?? 0), 0);
  const yesterdaySnowfall = yesterdayHours
    ? yesterdayHours.reduce((s, h) => s + (h.weather.snowfall_cm ?? 0), 0)
    : 0;
  const freshImpact = todaySnowfall + yesterdaySnowfall * 0.40;

  if (daylight.length === 0) {
    return {
      factor: DESCENT_FLOOR * 0.5, effectiveFreshCm: 0, freshImpactCm: freshImpact,
      rainPenalised: false, cornDetected: false, crustSeverity: 0, value: "No data",
    };
  }

  // Mid-mountain is the representative descent surface
  const lapseAdjC = 6.5 * (bands.mid - bands.queryElev) / 1000;
  const midWind = (w: WeatherHourly) => deriveMidWind(w);
  const lookback = buildLookback(hours, yesterdayHours);

  const depths = daylight
    .map((h) => h.weather.snow_depth_m)
    .filter((d): d is number => d !== null);
  const meanDepth = depths.length > 0 ? depths.reduce((a, b) => a + b, 0) / depths.length : null;

  const cornDetected = detectCorn(lookback, meanDepth, lapseAdjC);
  const localCrust = detectBreakableCrust(lookback, meanDepth, cornDetected, lapseAdjC);
  const crustSeverity = cornDetected
    ? localCrust.severity
    : Math.max(localCrust.severity, crustSeverityOverride ?? 0);

  const meanMidTempC = hours.reduce(
    (s, h) => s + lapseTemp(h.weather.temp_c, bands.queryElev, bands.mid), 0,
  ) / hours.length;

  const { effectiveFreshCm } = computeEffectiveFresh(
    lookback, 24 + anchorHour, bands.mid, meanMidTempC, lapseAdjC, midWind,
  );

  // Saturating map: 0cm → floor, ~10cm effective → most of the range,
  // deep preserved powder → 1.05
  let factor = DESCENT_FLOOR + DESCENT_RANGE * (1 - Math.exp(-effectiveFreshCm / DESCENT_SATURATION_CM));

  // Corn cycle is a prime touring descent regardless of fresh snow
  if (cornDetected) {
    factor = Math.max(factor, DESCENT_CORN_FLOOR);
  }

  // Breakable crust: the classic bad spring tour. Only bites when fresh
  // snow hasn't buried it and the cycle isn't producing corn.
  if (!cornDetected && crustSeverity > 0 && effectiveFreshCm < CRUST_COVERAGE_THRESHOLD_CM) {
    factor *= 1.0 - crustSeverity * DESCENT_CRUST_PENALTY;
  }

  let rainPenalised = false;
  const midElev = bands.mid;

  // Accumulated liquid water changes the surface progressively. Use only
  // elapsed rain, discounted once fresh snow buries the affected layer.
  const rainAtMid = (source: NormalisedHour[]) => source.reduce((sum, h) => {
    const warmEnough = h.weather.freezing_level_m !== null
      ? h.weather.freezing_level_m > midElev
      : h.weather.temp_c - lapseAdjC > 0;
    return sum + (warmEnough ? Math.max(0, h.weather.rain_mm ?? 0) + Math.max(0, h.weather.showers_mm ?? 0) : 0);
  }, 0);
  const todayRain = rainAtMid(hours);
  const oldRain = rainAtMid(yesterdayHours ?? []);
  const exposedFraction = Math.exp(-effectiveFreshCm / CRUST_COVERAGE_THRESHOLD_CM);
  const rainDose = todayRain + oldRain * 0.5;
  if (rainDose > 0) {
    factor *= 1 - exposedFraction * (1 - Math.exp(-rainDose / 4));
    rainPenalised = exposedFraction * rainDose > 0.3;
  }

  factor = clamp(factor, 0.05, 1.05);

  let value: string;
  if (rainPenalised) {
    value = effectiveFreshCm > 1
      ? `${Math.round(effectiveFreshCm)} cm effective fresh, rain damage`
      : "Rain-damaged snowpack";
  } else if (cornDetected) {
    value = "Corn cycle: softening descent";
  } else if (crustSeverity >= 0.4 && effectiveFreshCm < CRUST_COVERAGE_THRESHOLD_CM) {
    value = "Breakable crust likely";
  } else if (effectiveFreshCm > 1) {
    value = `${Math.round(effectiveFreshCm)} cm effective fresh`;
  } else {
    value = "Settled snow";
  }

  return { factor, effectiveFreshCm, freshImpactCm: freshImpact, rainPenalised, cornDetected, crustSeverity, value };
}

function touringContextAtHour(
  hours: NormalisedHour[],
  yesterday: NormalisedHour[] | undefined,
  bands: ReturnType<typeof deriveElevationBands>,
  hour: number,
  crustSeverityOverride?: number,
) {
  const available = uniqueOrderedHours(hours).filter((point) => point.hour <= hour);
  const descent = computeDescentQualityFactor(available, yesterday, bands, crustSeverityOverride, hour);
  return {
    descent,
    trailBreak: computeTrailBreakFactor(descent.freshImpactCm),
    freezeThaw: computeFreezeThawFactor(available, yesterday, bands),
    coverage: computeSnowCoverageFactor(available, bands),
  };
}

// ---------------------------------------------------------------------------
// Factor 2: Trail Breaking (constant for the day)
//
// Ascent-side cost of very deep unconsolidated fresh: exhausting skin track.
// ---------------------------------------------------------------------------

function computeTrailBreakFactor(freshImpactCm: number): number {
  if (freshImpactCm <= SNOW_TRAIL_BREAK_ONSET_CM) return 1.0;
  const excess = freshImpactCm - SNOW_TRAIL_BREAK_ONSET_CM;
  return SNOW_TRAIL_BREAK_FLOOR + (1 - SNOW_TRAIL_BREAK_FLOOR) * Math.exp(-(excess ** 2) / SNOW_TRAIL_BREAK_WIDTH);
}

// ---------------------------------------------------------------------------
// Factor 3: Freeze-Thaw Cycle (constant for the day, seasonally relevant)
// ---------------------------------------------------------------------------

type FreezeThawResult = { factor: number; overnightMin: number; warmingRate: number; value: string };

function computeFreezeThawFactor(
  hours: NormalisedHour[],
  yesterdayHours: NormalisedHour[] | undefined,
  bands: ReturnType<typeof deriveElevationBands>
): FreezeThawResult {
  const midElev = bands.mid;
  const daylight = hours.filter((h) => h.weather.is_day);
  const sunriseHour = hours.find((h) => h.weather.is_day)?.hour ?? 7;

  // --- Overnight refreeze quality ---
  const overnightTemps: number[] = [];
  if (yesterdayHours) {
    for (const h of yesterdayHours.filter((h) => h.hour >= 18)) {
      overnightTemps.push(lapseTemp(h.weather.temp_c, bands.queryElev, midElev));
    }
  }
  for (const h of hours.filter((h) => h.hour <= sunriseHour)) {
    overnightTemps.push(lapseTemp(h.weather.temp_c, bands.queryElev, midElev));
  }

  let overnightMin: number;
  if (overnightTemps.length === 0) {
    const preSunrise = hours.filter((h) => h.hour < sunriseHour);
    if (preSunrise.length > 0) {
      overnightMin = Math.min(...preSunrise.map((h) => lapseTemp(h.weather.temp_c, bands.queryElev, midElev)));
    } else {
      return { factor: 1.0, overnightMin: -10, warmingRate: 1.5, value: "No overnight data" };
    }
  } else {
    overnightMin = Math.min(...overnightTemps);
  }

  let refreezeScore: number;
  if (overnightMin > 0) {
    refreezeScore = 0.10;
  } else if (overnightMin > -2) {
    refreezeScore = 0.10 + 0.25 * (-overnightMin / 2);
  } else {
    // Gaussian centered at optimal refreeze temp
    refreezeScore = 0.35 + 0.65 * gaussian(overnightMin, FREEZE_THAW_REFREEZE_OPTIMAL_C, FREEZE_THAW_REFREEZE_SIGMA);
  }

  // Soil temperature modifier (thermal mass from below)
  const sunriseData = hours.find((h) => h.hour === sunriseHour);
  const soilTemp = sunriseData?.weather.soil_temp_0cm_c;
  if (soilTemp !== null && soilTemp !== undefined) {
    if (soilTemp > 2) refreezeScore *= 0.75;
    else if (soilTemp > 0) refreezeScore *= 0.88;
  }

  // --- Morning warming rate ---
  const sunrisePlus4 = sunriseHour + 4;
  const sunrise4Data = hours.find((h) => h.hour === sunrisePlus4);

  let warmingRate = 1.5;
  let warmingScore = 0.75;
  if (sunriseData && sunrise4Data) {
    const tempAtSunrise = lapseTemp(sunriseData.weather.temp_c, bands.queryElev, midElev);
    const tempAtPlus4 = lapseTemp(sunrise4Data.weather.temp_c, bands.queryElev, midElev);
    warmingRate = (tempAtPlus4 - tempAtSunrise) / 4;

    warmingScore = 0.25 + 0.75 * gaussian(warmingRate, WARMING_OPTIMAL_RATE, WARMING_SIGMA);

    // Solar confirmation: high radiation + good warming = genuine corn development
    const windowHours = hours.filter((h) => h.hour >= sunrisePlus4 - 2 && h.hour <= sunrisePlus4);
    if (windowHours.length > 0) {
      const meanRad = windowHours.reduce((s, h) => s + (h.weather.direct_radiation_wm2 ?? 0), 0) / windowHours.length;
      if (meanRad > 200 && warmingRate >= 1.0 && warmingRate <= 2.5) {
        warmingScore = Math.min(1.0, warmingScore * 1.10);
      } else if (meanRad < 50 && warmingRate >= 2.5) {
        // Warming without sun = foehn; wet snow, not corn
        warmingScore *= 0.65;
      }
    }
  }

  // Combine refreeze and warming multiplicatively with exponents
  const rawFT = Math.pow(clamp(refreezeScore, 0.01, 1.0), 0.65) * Math.pow(clamp(warmingScore, 0.01, 1.0), 0.35);

  // Freeze-thaw relevance: fades to neutral when temps are well below zero all day
  const maxMidTemp = daylight.length > 0
    ? Math.max(...daylight.map((h) => lapseTemp(h.weather.temp_c, bands.queryElev, midElev)))
    : -15;
  const ftRelevance = clamp((maxMidTemp + 10) / 15, 0, 1);

  // When relevance is low (deep winter), factor → 1.0 (neutral)
  const factor = 1.0 - ftRelevance * (1.0 - rawFT);

  let value: string;
  if (overnightMin > 0) value = "No freeze overnight";
  else if (overnightMin > -3) value = `Marginal freeze (${Math.round(overnightMin)}°C)`;
  else if (overnightMin > -8) value = `Good freeze (${Math.round(overnightMin)}°C)`;
  else if (overnightMin > -15) value = `Hard freeze (${Math.round(overnightMin)}°C)`;
  else value = `Deep freeze (${Math.round(overnightMin)}°C)`;

  return { factor: clamp(factor, 0.05, 1.0), overnightMin, warmingRate, value };
}

// ---------------------------------------------------------------------------
// Factor 4: Snow Coverage (constant for the day)
// ---------------------------------------------------------------------------

type CoverageResult = {
  factor: number;
  snowFraction: number;
  depthM: number | null;
  depthFactor: number | null;
  value: string;
};

function computeSnowCoverageFactor(
  hours: NormalisedHour[],
  bands: ReturnType<typeof deriveElevationBands>
): CoverageResult {
  const daylight = hours.filter((h) => h.weather.is_day);
  if (daylight.length === 0) {
    return {
      factor: 0.05,
      snowFraction: 0,
      depthM: null,
      depthFactor: null,
      value: "No data",
    };
  }

  // Freezing level → snow fraction
  // Use morning hours (when touring starts) rather than full-day average:
  // in spring the freezing level rises in the afternoon creating corn conditions,
  // but the tour starts when the snowpack is still frozen and accessible
  const morningDaylight = daylight.filter((h) => h.hour <= 11);
  const relevantHours = morningDaylight.length > 0 ? morningDaylight : daylight;
  const freezingLevels = relevantHours
    .map((h) => h.weather.freezing_level_m)
    .filter((f): f is number => f !== null);
  const meanFreezing = freezingLevels.length > 0
    ? freezingLevels.reduce((a, b) => a + b, 0) / freezingLevels.length
    : bands.valley;

  const verticalRange = bands.summit - bands.valley;
  const snowFraction = verticalRange > 0
    ? clamp((bands.summit - meanFreezing) / verticalRange, 0, 1)
    : 1;

  // Snow fraction factor: power curve with floor
  const fractionFactor = 0.05 + 0.95 * Math.pow(snowFraction, 0.7);

  // Snow depth factor: sigmoid
  const snowDepths = daylight
    .map((h) => h.weather.snow_depth_m)
    .filter((d): d is number => d !== null);
  const meanDepth = snowDepths.length > 0
    ? snowDepths.reduce((a, b) => a + b, 0) / snowDepths.length
    : null;

  const depthFactor = meanDepth === null
    ? null
    : sigmoidRising(meanDepth, DEPTH_MIDPOINT_M, DEPTH_STEEPNESS, 0.05);

  // When confirmed deep snowpack exists, freezing level alone shouldn't dominate:
  // established winter snowpack persists well above the current freezing level
  let effectiveFraction = fractionFactor;
  if (meanDepth !== null && meanDepth >= 1.0) {
    effectiveFraction = Math.max(fractionFactor, 0.94);
  } else if (meanDepth !== null && meanDepth >= 0.8) {
    effectiveFraction = Math.max(fractionFactor, 0.88);
  }

  // With unknown depth, retain only the freezing-level coverage diagnostic.
  // The canonical caller publishes no touring score for this state.
  const diagnosticFactor = depthFactor === null
    ? effectiveFraction
    : effectiveFraction * depthFactor;
  const factor = clamp(diagnosticFactor, 0.05, 1.0);
  const value = meanDepth !== null
    ? `${Math.round(snowFraction * 100)}% coverage, ${(meanDepth * 100).toFixed(0)} cm depth`
    : `${Math.round(snowFraction * 100)}% snow coverage`;

  return { factor, snowFraction, depthM: meanDepth, depthFactor, value };
}

// ---------------------------------------------------------------------------
// Factor 5: Wind (hourly)
// ---------------------------------------------------------------------------

function computeWindFactor(h: NormalisedHour, bands: ReturnType<typeof deriveElevationBands>): number {
  const summitWind = deriveSummitWind(h.weather);

  // Sigmoid decay: calm → severe
  let factor = sigmoid(summitWind, WIND_MIDPOINT_KMH, WIND_STEEPNESS, WIND_FLOOR);

  // Gust penalty: smooth degradation for gusty conditions
  const summitGust = deriveSummitGust(h.weather);
  const gustRatio = summitGust / Math.max(summitWind, 1);
  if (gustRatio > 1.3 && summitGust > 35) {
    const gustPenalty = 1.0 - 0.15 * Math.min(1, (gustRatio - 1.3) / 0.7) * Math.min(1, (summitGust - 35) / 30);
    factor *= gustPenalty;
  }

  // Extreme wind chill interaction: exposed ridgeline in extreme cold
  const summitTemp = lapseTemp(h.weather.temp_c, bands.queryElev, bands.summit);
  const summitWC = windChill(summitTemp, summitWind);
  if (summitWC < -35) {
    factor *= 0.70;
  } else if (summitWC < -28) {
    factor *= 1.0 - 0.30 * ((summitWC + 28) / -7);
  }

  return clamp(factor, 0.05, 1.0);
}

// ---------------------------------------------------------------------------
// Factor 6: Visibility (hourly)
// ---------------------------------------------------------------------------

function computeVisibilityFactor(h: NormalisedHour): number {
  const baseVis = h.weather.visibility_m ?? 20000;
  const midImmersed = isCloudImmersedMid(h.weather);
  const summitImmersed = isCloudImmersedSummit(h.weather);

  // Elevation-weighted visibility
  const valleyVis = baseVis;
  const midVis = midImmersed ? baseVis * 0.6 : baseVis;
  const summitVis = summitImmersed ? baseVis * 0.3 : baseVis * 0.7;
  const weightedVis = valleyVis * 0.15 + midVis * 0.50 + summitVis * 0.35;

  // Sigmoid rising: poor vis → good vis
  let factor = sigmoidRising(weightedVis, VIS_MIDPOINT_M, VIS_STEEPNESS, VIS_FLOOR);

  // Cloud immersion compounds with base visibility penalty
  if (midImmersed && summitImmersed) {
    factor *= 0.40;
  } else if (summitImmersed) {
    factor *= 0.65;
  }

  // Fog/rime fog: severe penalty
  if (h.weather.weather_code === 45 || h.weather.weather_code === 48) {
    factor = Math.min(factor, 0.15);
  }

  return clamp(factor, 0.05, 1.0);
}

// ---------------------------------------------------------------------------
// Factor 7: Precipitation (hourly)
// ---------------------------------------------------------------------------

function computePrecipFactor(h: NormalisedHour, _bands: ReturnType<typeof deriveElevationBands>): number {
  const snowfallCm = h.weather.snowfall_cm ?? 0;
  const rainMm = (h.weather.rain_mm ?? 0) + (h.weather.showers_mm ?? 0);
  const precip = h.weather.precip_mm;

  // Thunderstorm codes deliberately do NOT floor this factor: lightning on
  // exposed terrain is the safety layer's call (computeSafetyIndicators
  // fires a red thunderstorm flag); storm precipitation is penalised below
  // in millimetres like any other precipitation.

  // No precipitation: perfect
  if (precip <= 0.1 && snowfallCm <= 0 && rainMm <= 0) return 1.0;

  // Rain at touring altitude: catastrophic
  if (rainMm > 0) {
    // Exponential decay: 0.5mm → 0.40, 1mm → 0.22, 2mm → 0.09, 3mm+ → ~0.06
    return Math.max(0.05, 0.05 + 0.55 * Math.exp(-rainMm * 1.8));
  }

  // Snow during tour: mildly penalising (adds to descent quality, but reduces vis/effort)
  if (snowfallCm > 0) {
    return 0.35 + 0.65 * Math.exp(-snowfallCm / 2.5);
  }

  // Generic precipitation fallback
  return Math.max(0.20, 1.0 - precip * 0.25);
}

// ---------------------------------------------------------------------------
// Factor 8: Skinning Comfort (hourly)
// ---------------------------------------------------------------------------

function computeComfortFactor(h: NormalisedHour, bands: ReturnType<typeof deriveElevationBands>): number {
  // Comfort uses actual mid-band temperature (the wind factor carries wind chill
  // separately). Neutral across the gear-mitigated touring range; bites only at
  // genuine extremes (audit 13 V2).
  const midTemp = lapseTemp(h.weather.temp_c, bands.queryElev, bands.mid);

  let factor = 1.0;

  // Genuine extreme cold: below COMFORT_EXTREME_COLD_C the day degrades whatever
  // you wear, so a bounded dent to the floor remains; the gear-mitigated band
  // above it is not charged (clause 4). Deep cold also surfaces via the wind
  // factor's wind-chill term and the extreme_cold safety flag.
  if (midTemp < COMFORT_EXTREME_COLD_C) {
    factor = clamp(1.0 - (COMFORT_EXTREME_COLD_C - midTemp) * COMFORT_EXTREME_COLD_SLOPE, COMFORT_FLOOR, 1.0);
  }

  // Warm + humid skinning: genuine sweating discomfort on the ascent, not
  // priced by the snow factors (the dry-warm band is already charged by
  // freeze-thaw and the descent wet/rain penalties, so it is not charged here).
  const humidity = h.weather.humidity_pct ?? 50;
  if (humidity > 80 && midTemp > 0) {
    const humidityPenalty = 1.0 - 0.15 * Math.min(1, (humidity - 80) / 15) * Math.min(1, midTemp / 5);
    factor *= humidityPenalty;
  }

  return clamp(factor, COMFORT_FLOOR, 1.0);
}

// ---------------------------------------------------------------------------
// Factor 9: Solar & Sky (hourly)
// ---------------------------------------------------------------------------

function computeSolarFactor(h: NormalisedHour, bands: ReturnType<typeof deriveElevationBands>): number {
  const w = h.weather;

  // Flat light, not a sunshine tax (audit 13 V1 / 13 B2). A high-altitude
  // overcast deck (cirrostratus/altostratus above the rider) flattens surface
  // definition on the open alpine terrain where touring descents happen, WITHOUT
  // reducing visibility. F_visibility prices being IN low/mid cloud (immersion),
  // so keying flat light off the HIGH deck charges the common grey-but-not-
  // immersed day ONCE here, not twice (a narrow full-low-stratus-plus-high-cirrus
  // corner can still take both F_visibility's summit immersion and this dent, but
  // that is a genuine whiteout). Touring descents are inherently open terrain.
  // Bounded to the floor: flat light dents but never halves a grey powder day;
  // the power curve concentrates the penalty on genuine overcast (broken cloud
  // still casts shadows and definition). High cloud is physically <= total, so
  // min() both guarantees monotone-up vs the old total-cloud curve (a <= total
  // fraction over a higher floor) and falls back to total when the high band is null.
  const total = w.cloud_cover_pct / 100;
  const high = w.cloud_cover_high_pct != null ? w.cloud_cover_high_pct / 100 : total;
  const highCloudFraction = Math.min(high, total);
  let factor = SOLAR_FLAT_LIGHT_FLOOR + (1 - SOLAR_FLAT_LIGHT_FLOOR) * (1 - Math.pow(highCloudFraction, 2.5));

  // UV bluebird bonus (spring touring dream: sun + cold + clear), gated on a
  // genuinely clear total sky and capped at a 5% micro-bonus.
  const midTemp = lapseTemp(w.temp_c, bands.queryElev, bands.mid);
  const uv = w.uv_index;
  if (uv > 3 && w.cloud_cover_pct / 100 < 0.30 && midTemp < 0) {
    factor = Math.min(1.05, factor * 1.05);
  }

  return clamp(factor, SOLAR_FLAT_LIGHT_FLOOR, 1.05);
}

// ---------------------------------------------------------------------------
// Hourly score computation (purely multiplicative)
// ---------------------------------------------------------------------------

type HourlyFactors = {
  wind: number;
  visibility: number;
  precip: number;
  comfort: number;
  solar: number;
};

function computeHourlyFactors(
  h: NormalisedHour,
  bands: ReturnType<typeof deriveElevationBands>
): HourlyFactors {
  return {
    wind: computeWindFactor(h, bands),
    visibility: computeVisibilityFactor(h),
    precip: computePrecipFactor(h, bands),
    comfort: computeComfortFactor(h, bands),
    solar: computeSolarFactor(h, bands),
  };
}

function computeHourlyScore(
  hourlyFactors: HourlyFactors,
  descentFactor: number,
  trailBreakFactor: number,
  freezeThawFactor: number,
  coverageFactor: number
): number {
  const raw = 100 * composeScore(TOURING_CHAIN, {
    descent: descentFactor,
    trailBreak: trailBreakFactor,
    freezeThaw: freezeThawFactor,
    coverage: coverageFactor,
    wind: hourlyFactors.wind,
    visibility: hourlyFactors.visibility,
    precip: hourlyFactors.precip,
    comfort: hourlyFactors.comfort,
    solar: hourlyFactors.solar,
  }).compressed;
  return clamp(raw, 0, 100);
}

// ---------------------------------------------------------------------------
// Daily score derivation from hourly scores
// ---------------------------------------------------------------------------

const EARLY_WINDOW_MARGIN = 3;

export function findBestConsecutiveWindow(scores: number[], windowSize: number): { avg: number; startIdx: number } {
  if (scores.length < windowSize) {
    const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    return { avg, startIdx: 0 };
  }

  let bestAvg = -Infinity;
  let bestIdx = 0;

  for (let i = 0; i <= scores.length - windowSize; i++) {
    let sum = 0;
    for (let j = 0; j < windowSize; j++) sum += scores[i + j];
    const avg = sum / windowSize;

    // Early-start bias: a later window must beat the incumbent by a clear
    // margin to displace it (tourers prefer early starts)
    if (bestAvg === -Infinity || avg > bestAvg + EARLY_WINDOW_MARGIN) {
      bestAvg = avg;
      bestIdx = i;
    }
  }

  return { avg: bestAvg, startIdx: bestIdx };
}

// ---------------------------------------------------------------------------
// Safety indicators (independent of score)
// ---------------------------------------------------------------------------

function computeSafetyIndicators(
  hours: NormalisedHour[],
  yesterdayHours: NormalisedHour[] | undefined,
  bands: ReturnType<typeof deriveElevationBands>,
  snowQuality: Pick<DescentQualityResult, "freshImpactCm">,
  dailySources?: DailySources
): TouringSafetyIndicator[] {
  const indicators: TouringSafetyIndicator[] = [];
  const daylight = hours.filter((h) => h.weather.is_day);

  // 1. Avalanche danger level (from bulletin data if available)
  const dangerLevel = dailySources?.avalanche?.danger_level;
  if (dangerLevel !== null && dangerLevel !== undefined) {
    let severity: "green" | "amber" | "red";
    let label: string;
    if (dangerLevel <= 2) {
      severity = "green";
      label = `Avalanche danger: ${dangerLevel}/5 (${dangerLevel === 1 ? "Low" : "Moderate"})`;
    } else if (dangerLevel === 3) {
      severity = "amber";
      label = "Avalanche danger: 3/5 (Considerable)";
    } else {
      severity = "red";
      label = `Avalanche danger: ${dangerLevel}/5 (${dangerLevel === 4 ? "High" : "Very High"})`;
    }
    indicators.push({
      key: "avalanche_bulletin",
      severity,
      label,
      detail: dailySources?.avalanche?.problems?.length
        ? `Problems: ${dailySources.avalanche.problems.join(", ")}`
        : null,
      disclaimer: "Always check your national avalanche bulletin before touring.",
    });
  } else {
    // Weather-derived avalanche proxy when no bulletin available
    const meanSummitWind = daylight.length > 0
      ? daylight.reduce((s, h) => s + deriveSummitWind(h.weather), 0) / daylight.length
      : 0;
    const freshSnow = snowQuality.freshImpactCm;

    // Wind loading proxy: wind + fresh snow
    const loadingIndex = meanSummitWind * freshSnow / 100;
    if (loadingIndex > 20 || (freshSnow > 30 && meanSummitWind > 30)) {
      indicators.push({
        key: "wind_loading",
        severity: "red",
        label: "High wind loading on fresh snow",
        detail: `${Math.round(freshSnow)} cm fresh with ${Math.round(meanSummitWind)} km/h summit wind. Elevated slab risk on lee slopes.`,
        disclaimer: "Always check your national avalanche bulletin before touring.",
      });
    } else if (loadingIndex > 8 || freshSnow > 20) {
      indicators.push({
        key: "wind_loading",
        severity: "amber",
        label: "Moderate wind loading",
        detail: `${Math.round(freshSnow)} cm fresh snow${meanSummitWind > 25 ? ` with ${Math.round(meanSummitWind)} km/h summit wind` : ""}.`,
        disclaimer: "Always check your national avalanche bulletin before touring.",
      });
    }
  }

  // 2. Rapid warming (wet slide proxy)
  if (daylight.length >= 4) {
    const midTemps = daylight.map((h) => lapseTemp(h.weather.temp_c, bands.queryElev, bands.mid));
    const earlyTemp = midTemps[0];
    const lateTemp = midTemps[Math.min(5, midTemps.length - 1)];
    const rate = (lateTemp - earlyTemp) / Math.min(6, midTemps.length);
    if (rate > 3.5) {
      indicators.push({
        key: "rapid_warming",
        severity: "red",
        label: "Very rapid warming",
        detail: `Temperature rising ${rate.toFixed(1)}°C/hr at mid-mountain. High wet slide risk on steep sun-exposed slopes.`,
        disclaimer: null,
      });
    } else if (rate > 2.5) {
      indicators.push({
        key: "rapid_warming",
        severity: "amber",
        label: "Rapid warming",
        detail: `Temperature rising ${rate.toFixed(1)}°C/hr. Plan descent timing carefully on sun-exposed aspects.`,
        disclaimer: null,
      });
    }
  }

  // 3. Extreme wind chill
  const summitWCs = daylight.map((h) => {
    const sTemp = lapseTemp(h.weather.temp_c, bands.queryElev, bands.summit);
    return windChill(sTemp, deriveSummitWind(h.weather));
  });
  const minWC = summitWCs.length > 0 ? Math.min(...summitWCs) : 0;
  if (minWC < -35) {
    indicators.push({
      key: "extreme_cold",
      severity: "red",
      label: "Extreme wind chill at summit",
      detail: `Wind chill ${Math.round(minWC)}°C at summit elevation. Frostbite risk in under 10 minutes on exposed skin.`,
      disclaimer: null,
    });
  } else if (minWC < -25) {
    indicators.push({
      key: "extreme_cold",
      severity: "amber",
      label: "Severe wind chill at summit",
      detail: `Wind chill ${Math.round(minWC)}°C at summit. Ensure full coverage for transitions.`,
      disclaimer: null,
    });
  }

  // 4. Thunderstorm risk
  const capeValues = daylight.map((h) => h.weather.cape_jkg ?? 0);
  const maxCape = Math.max(...capeValues, 0);
  const thunderHours = daylight.filter((h) => h.weather.weather_code >= 95 && h.weather.weather_code <= 99);
  if (thunderHours.length > 0 || maxCape > 1000) {
    indicators.push({
      key: "thunderstorm",
      severity: "red",
      label: "Thunderstorm risk",
      detail: thunderHours.length > 0
        ? "Thunderstorms forecast. Exposed alpine terrain is extremely dangerous in lightning."
        : `High convective energy (CAPE ${Math.round(maxCape)} J/kg). Monitor conditions closely.`,
      disclaimer: null,
    });
  }

  return indicators;
}

// ---------------------------------------------------------------------------
// Elevation profile builder
// ---------------------------------------------------------------------------

function buildElevationProfile(
  hours: NormalisedHour[],
  bands: ReturnType<typeof deriveElevationBands>
): ElevationBandHour[][] {
  const sunriseHour = hours.find((h) => h.weather.is_day)?.hour ?? 7;
  const sunsetHour = [...hours].reverse().find((h) => h.weather.is_day)?.hour ?? 18;
  const startHour = Math.max(0, sunriseHour - 1);
  const endHour = Math.min(23, sunsetHour);
  const displayHours = hours.filter((h) => h.hour >= startHour && h.hour <= endHour).slice(0, 16);

  const bandDefs: Array<{ band: "valley" | "mid" | "summit"; elev: number }> = [
    { band: "summit", elev: bands.summit },
    { band: "mid", elev: bands.mid },
    { band: "valley", elev: bands.valley },
  ];

  return bandDefs.map(({ band, elev }) =>
    displayHours.map((h) => {
      const temp = lapseTemp(h.weather.temp_c, bands.queryElev, elev);
      let windKmh: number;
      if (band === "valley") windKmh = h.weather.wind_speed_10m_kmh;
      else if (band === "mid") windKmh = deriveMidWind(h.weather);
      else windKmh = deriveSummitWind(h.weather);

      const cloudImmersed = band === "mid"
        ? isCloudImmersedMid(h.weather)
        : band === "summit"
          ? isCloudImmersedSummit(h.weather)
          : false;

      const freezingLevel = h.weather.freezing_level_m ?? 0;
      const precipType = h.weather.precip_mm > 0
        ? getPrecipType(h.weather.freezing_level_m, elev)
        : "none" as const;

      const vis = deriveVisibility(h.weather.visibility_m, band, cloudImmersed);

      return {
        elevation_m: elev,
        band,
        hour: h.hour,
        temp_c: Math.round(temp * 10) / 10,
        wind_kmh: Math.round(windKmh),
        precip_type: precipType,
        cloud_immersed: cloudImmersed,
        visibility_m: Math.round(vis),
        freezing_level_m: freezingLevel,
      };
    })
  );
}

// ---------------------------------------------------------------------------
// Main entry: Daily score
// ---------------------------------------------------------------------------

export function calculateSkiTouringDailyV4(
  hours: NormalisedHour[],
  config: ElevationConfig,
  yesterdayHours?: NormalisedHour[],
  date?: string,
  dailySources?: DailySources,
  crustSeverityOverride?: number,
  bandAnchors?: BandAnchors,
): TouringDailyResult {
  if (hours.length === 0) {
    return {
      score: 0,
      label: "Flat",
      snowScore: 0,
      windowScore: 0,
      freezeThawScore: 0,
      bestWindowStart: null,
      suppressed: true,
      suppressionReason: "No hourly data",
      coverageClassification: "unknown",
      factors: [],
      elevationProfile: [],
      stabilityDisclaimer: true,
      safety: [],
    };
  }

  const bands = deriveElevationBands(config);

  // Phase-3: the SCORE-driving depth consumers (suppression gate, descent
  // quality, snow coverage) read the interpolated natural depth at the mid band
  // (the representative descent surface). DISPLAY/FLAG functions
  // (buildElevationProfile, computeSafetyIndicators) and the non-depth factors
  // (freezeThaw, the hourly daylight loop) keep the RAW hours — the elevation
  // profile must show the model's real per-band data and avalanche flags read
  // raw. Absent/<2 anchors → scoringHours IS hours (byte-identical to today).
  const scoringHours = applyInterpDepthAtMid(hours, bands, config.elevation_m, bandAnchors);

  // Suppression check
  const suppression = checkSuppressions(scoringHours, bands);
  if (suppression.suppressed) {
    return {
      score: 0,
      label: "Flat",
      snowScore: 0,
      windowScore: 0,
      freezeThawScore: 0,
      bestWindowStart: null,
      suppressed: true,
      suppressionReason: suppression.reason,
      coverageClassification:
        suppression.reason === "Insufficient snow cover" ? "fail" : "unknown",
      factors: [],
      elevationProfile: buildElevationProfile(hours, bands),
      stabilityDisclaimer: true,
      safety: computeSafetyIndicators(hours, yesterdayHours, bands, { freshImpactCm: 0 }, dailySources),
    };
  }

  // Compute hourly scores for daylight hours
  const daylight = uniqueOrderedHours(hours).filter((h) => h.weather.is_day);
  const hourlyScores: number[] = [];
  const hourlyFactorsList: HourlyFactors[] = [];

  for (const h of daylight) {
    const factors = computeHourlyFactors(h, bands);
    hourlyFactorsList.push(factors);
    const context = touringContextAtHour(scoringHours, yesterdayHours, bands, h.hour, crustSeverityOverride);
    hourlyScores.push(computeHourlyScore(factors, context.descent.factor, context.trailBreak, context.freezeThaw.factor, context.coverage.factor));
  }

  // Daily derivation: best 5h window + daylight average
  const daylightAvg = hourlyScores.length > 0
    ? hourlyScores.reduce((a, b) => a + b, 0) / hourlyScores.length
    : 0;

  const candidates = consecutiveHourlyWindows(daylight.map((h, index) => ({ hour: h.hour, score: hourlyScores[index] })), TOURING_WINDOW_HOURS);
  let bestWindow = { avg: daylightAvg, startIdx: -1 };
  let bestWindowStartHour: number | null = null;
  for (const window of candidates) {
    const avg = window.reduce((sum, h) => sum + h.score, 0) / window.length;
    if (bestWindowStartHour === null || avg > bestWindow.avg + EARLY_WINDOW_MARGIN) {
      bestWindowStartHour = window[0].hour;
      bestWindow = { avg, startIdx: daylight.findIndex((h) => h.hour === window[0].hour) };
    }
  }
  const summaryHour = bestWindowStartHour === null
    ? daylight[Math.floor(daylight.length / 2)]?.hour ?? 12
    : bestWindowStartHour + Math.floor(TOURING_WINDOW_HOURS / 2);
  const { descent, trailBreak, freezeThaw, coverage } = touringContextAtHour(
    scoringHours, yesterdayHours, bands, summaryHour, crustSeverityOverride,
  );
  const coverageClassification: TouringDailyResult["coverageClassification"] = coverage.depthM === null
    ? "unknown" : coverage.depthM < 0.1 ? "fail" : coverage.depthM < 0.4 ? "marginal" : "pass";

  // Daily score on 0-10 scale
  let dailyScore = (bestWindow.avg * DAILY_BEST_WINDOW_WEIGHT + daylightAvg * DAILY_DAYLIGHT_AVG_WEIGHT) / 10;

  // Rain already changes snow texture and the current hour's precipitation
  // factor. Do not apply another whole-day cap from a later rain event.

  dailyScore = clamp(Math.round(dailyScore * 10) / 10, 0, 10);

  // Factor scores for UI display (0-10 scale for consistency)
  const snowScore = clamp(Math.round(descent.factor * 100) / 10, 0, 10);
  const ftScore = clamp(Math.round(freezeThaw.factor * 100) / 10, 0, 10);
  const coverageScore = clamp(Math.round(coverage.factor * 100) / 10, 0, 10);
  const trailBreakScore = clamp(Math.round(trailBreak * 100) / 10, 0, 10);

  const windowScore = clamp(Math.round(bestWindow.avg / 10 * 10) / 10, 0, 10);

  const factors: TouringFactor[] = [
    { key: "descent_quality", label: "Descent quality", score: snowScore, weight: 1.0, value: descent.value },
    { key: "freeze_thaw", label: "Freeze-thaw cycle", score: ftScore, weight: 1.0, value: freezeThaw.value },
    { key: "snow_coverage", label: "Snow coverage", score: coverageScore, weight: 1.0, value: coverage.value },
    ...(trailBreak < 0.97
      ? [{ key: "trail_break", label: "Trail breaking", score: trailBreakScore, weight: 1.0, value: `${Math.round(descent.freshImpactCm)} cm unconsolidated fresh, exhausting skin track` }]
      : []),
    { key: "touring_window", label: "Touring window", score: windowScore, weight: 1.0, value: bestWindowStartHour === null ? "Available hours only; no continuous five-hour window" : `Best ${TOURING_WINDOW_HOURS}h from ${String(bestWindowStartHour).padStart(2, "0")}:00` },
  ];

  const safety = computeSafetyIndicators(hours, yesterdayHours, bands, descent, dailySources);

  return {
    score: dailyScore,
    label: touringV4Label(dailyScore),
    snowScore,
    windowScore,
    freezeThawScore: ftScore,
    bestWindowStart: bestWindowStartHour,
    suppressed: false,
    suppressionReason: null,
    coverageClassification,
    factors,
    elevationProfile: buildElevationProfile(hours, bands),
    stabilityDisclaimer: true,
    safety,
  };
}

// ---------------------------------------------------------------------------
// Hourly scores export (for detail page hourly graph)
// ---------------------------------------------------------------------------

/** Touring keeps its ascent effort and weather response; cover is applied once. */
export function scoreTouringSnowStateHour(h: NormalisedHour, snow: { cover: number; freshCm: number; crust: number; liquidFraction: number }): number {
  const bands = deriveElevationBands({ elevation_m: 2000, elevation_base_m: 2000, mid_elevation_m: 2000, elevation_summit_m: 2000 });
  const descent = clamp((0.65 + 0.4 * (1 - Math.exp(-snow.freshCm / 15))) * (1 - 0.4 * snow.crust) * (1 - Math.min(0.35, snow.liquidFraction * 5)), 0.2, 1.05);
  return computeHourlyScore(computeHourlyFactors(h, bands), descent, computeTrailBreakFactor(snow.freshCm), 1, 1) * snow.cover;
}

export function computeTouringHourlyScores(
  hours: NormalisedHour[],
  config: ElevationConfig,
  yesterdayHours?: NormalisedHour[],
  crustSeverityOverride?: number,
  bandAnchors?: BandAnchors,
): number[] {
  const bands = deriveElevationBands(config);

  // Phase-3: depth consumers (descent quality, snow coverage) read the
  // interpolated mid-band natural depth; the hourly daylight loop below uses
  // computeHourlyFactors which does NOT read depth, so it keeps raw hours.
  // Absent/<2 anchors → scoringHours IS hours (byte-identical to today).
  const scoringHours = applyInterpDepthAtMid(hours, bands, config.elevation_m, bandAnchors);

  return hours.map((h) => {
    if (!h.weather.is_day) return 0;
    const factors = computeHourlyFactors(h, bands);
    const context = touringContextAtHour(scoringHours, yesterdayHours, bands, h.hour, crustSeverityOverride);
    const raw = computeHourlyScore(factors, context.descent.factor, context.trailBreak, context.freezeThaw.factor, context.coverage.factor);
    // Convert 0-100 hourly to 0-10 for consistency with other sport hourly scores
    return clamp(Math.round(raw / 10 * 10) / 10, 0, 10);
  });
}

// ---------------------------------------------------------------------------
// Elevation bands
//
// A tour spans a vertical range, so touring bands are RANGES, not points:
// "lower mountain" (valley to mid) and "upper mountain" (mid to summit).
// Each band rescores the day with a narrowed ElevationConfig; the scorer's
// own valley/mid/summit machinery handles all lapse adjustment internally
// from the fetch elevation, so no hour transform is needed and the
// freezing-level, coverage and freeze-thaw logic follow the band's range
// automatically. Zero extra fetches or storage.
// ---------------------------------------------------------------------------

const TOURING_BAND_MIN_SEPARATION_M = 150;

export type TouringElevationBand = {
  band: "lower" | "upper";
  fromM: number;
  toM: number;
  score: number;
  label: string;
};

/**
 * `config.elevation_m` must remain the elevation the hourly data was fetched
 * at; only the base/summit range narrows per band. Returns [] when the
 * resort's vertical is too small to split meaningfully.
 */
export function calculateTouringElevationBands(
  hours: NormalisedHour[],
  config: ElevationConfig,
  yesterdayHours?: NormalisedHour[],
  date?: string,
  crustSeverityOverride?: number,
  bandAnchors?: BandAnchors,
): TouringElevationBand[] {
  const summit = config.elevation_summit_m ?? config.elevation_m + 400;
  const valley = config.elevation_base_m ?? config.elevation_m - 800;
  const mid = config.mid_elevation_m ?? Math.round((valley + summit) / 2);

  if (mid - valley < TOURING_BAND_MIN_SEPARATION_M || summit - mid < TOURING_BAND_MIN_SEPARATION_M) {
    return [];
  }

  function scoreBand(band: TouringElevationBand["band"], fromM: number, toM: number): TouringElevationBand {
    const result = calculateSkiTouringDailyV4(
      hours,
      { elevation_m: config.elevation_m, elevation_base_m: fromM, elevation_summit_m: toM },
      yesterdayHours,
      date,
      undefined,
      crustSeverityOverride,
      // Phase-3: each band narrows the config → a different bands.mid, so the
      // same anchor set interpolates the natural depth at each band's own mid
      // (the lower band reads thinner than the upper under a real gradient).
      bandAnchors,
    );
    return { band, fromM, toM, score: result.score, label: result.label };
  }

  return [
    scoreBand("lower", valley, mid),
    scoreBand("upper", mid, summit),
  ];
}
